"use client";
import MuiAutocomplete from "@mui/material/Autocomplete";
import { type ReactNode } from "react";
import ErrorMessage from "@/components/common/formElements/controlled/ErrorMessage";
import {
  type AutocompleteOption,
  useAutocompleteFieldController,
  type UseAutocompleteFieldControllerProps,
} from "@/components/common/formElements/hooks/useAutocompleteFieldController";
import AutocompleteTextInput from "@/components/common/formElements/library/AutocompleteTextInput";
import type { AutocompleteProps as MuiAutocompleteProps } from "@mui/material/Autocomplete";
import type { TextFieldVariants } from "@mui/material/TextField";
import type { FieldValues } from "react-hook-form";

type AutocompletePropsWithControllerProps<
  TValue extends AutocompleteOption,
  TFieldValues extends FieldValues = FieldValues,
> = UseAutocompleteFieldControllerProps<TValue, TFieldValues> &
  Partial<
    Omit<
      MuiAutocompleteProps<TValue, boolean, boolean, boolean>,
      | "name"
      | "value"
      | "defaultValue"
      | "onChange"
      | "onBlur"
      | "error"
      | "options"
    >
  >;

export interface AutocompleteFieldProps<
  TValue extends AutocompleteOption,
  TFieldValues extends FieldValues = FieldValues,
> extends AutocompletePropsWithControllerProps<TValue, TFieldValues> {
  required?: boolean;
  label?: string;
  helperText?: string;
  placeholder?: string;
  variant?: TextFieldVariants;
  tooltip?: ReactNode;
}

export default function AutocompleteField<
  TValue extends AutocompleteOption,
  TFieldValues extends FieldValues,
>(props: AutocompleteFieldProps<TValue, TFieldValues>) {
  const {
    fullWidth = true,
    name,
    onChange,
    label,
    placeholder,
    variant = "outlined",
    helperText,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    options,
    required: externalRequired,
    tooltip,
    ...restAutocompleteProps
  } = props;

  const {
    field,
    required,
    onChangeField,
    formState: { errors },
    fieldState: { invalid: error },
    optionsLabelMapper,
  } = useAutocompleteFieldController<TValue, TFieldValues>({
    name,
    onChange,
    defaultValue,
    rules,
    shouldUnregister,
    disabled,
    options,
  });

  return (
    <MuiAutocomplete<TValue, boolean, boolean, boolean>
      {...restAutocompleteProps}
      fullWidth={fullWidth}
      value={field.value}
      options={options}
      getOptionDisabled={(option) => !!option.disabled}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === "string") {
          return option.value === value;
        }

        return option.value === value.value;
      }}
      getOptionLabel={(selectedValue) => {
        if (typeof selectedValue === "string") {
          return optionsLabelMapper[selectedValue]?.toString() || "";
        }

        return selectedValue.label || "";
      }}
      onChange={onChangeField}
      renderInput={(params) => (
        <AutocompleteTextInput
          {...params}
          inputRef={field.ref}
          label={label}
          error={error}
          placeholder={placeholder}
          required={externalRequired || required}
          variant={variant}
          tooltip={tooltip}
          helperText={
            error ? (
              <ErrorMessage name={field.name} errors={errors} />
            ) : (
              helperText
            )
          }
        />
      )}
    />
  );
}
